import {
  CanvasDimensions,
  ImageResolution,
  SpecialZoneType,
  ZonePolygonData,
} from '@hakimo-ui/hakimo/ui-elements';
import { MutableRefObject, useCallback, useState } from 'react';
import { useZoneCRUD } from './useZoneCRUD';
import {
  getInitialZone,
  isNewZone,
  removeZone,
  resetZoneToInitial,
} from './zoneHelpers';

interface UseZoneActionsParams {
  zones: ZonePolygonData[];
  setZones: React.Dispatch<React.SetStateAction<ZonePolygonData[]>>;
  canvasDimensions: CanvasDimensions | null;
  imageResolution: ImageResolution | null;
  initialZonesRef: MutableRefObject<ZonePolygonData[]>;
  initialZoneIdsRef: MutableRefObject<Set<string>>;
  saveZonesByType: (
    zones: ZonePolygonData[],
    type: SpecialZoneType,
    canvasDimensions: CanvasDimensions,
    imageResolution: ImageResolution,
    onSuccess?: () => void
  ) => void;
}

export function useZoneActions({
  zones,
  setZones,
  canvasDimensions,
  imageResolution,
  initialZonesRef,
  initialZoneIdsRef,
  saveZonesByType,
}: UseZoneActionsParams) {
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);

  const handleZoneChange = useCallback(
    (updatedZone: ZonePolygonData) => {
      setZones((prev) =>
        prev.map((zone) => (zone.id === updatedZone.id ? updatedZone : zone))
      );
    },
    [setZones]
  );

  const discardUnsavedChanges = useCallback(
    (zoneId: string) => {
      if (isNewZone(zoneId, initialZoneIdsRef)) {
        setZones((prev) => removeZone(prev, zoneId));
        return;
      }
      const initial = getInitialZone(zoneId, initialZonesRef);
      if (initial) {
        setZones((prev) => resetZoneToInitial(prev, zoneId, initial));
      }
    },
    [setZones, initialZonesRef, initialZoneIdsRef]
  );

  const handleSelectZone = useCallback(
    (zoneId: string | null) => {
      if (selectedZoneId && selectedZoneId !== zoneId) {
        discardUnsavedChanges(selectedZoneId);
      }
      setSelectedZoneId(zoneId);
    },
    [selectedZoneId, discardUnsavedChanges]
  );

  const { handleAddZone, handleResetZone, handleSaveZone, handleDeleteZone } =
    useZoneCRUD({
      zones,
      setZones,
      canvasDimensions,
      imageResolution,
      initialZonesRef,
      initialZoneIdsRef,
      selectedZoneId,
      setSelectedZoneId,
      saveZonesByType,
    });

  return {
    selectedZoneId,
    handleZoneChange,
    handleSelectZone,
    handleAddZone,
    handleResetZone,
    handleSaveZone,
    handleDeleteZone,
  };
}
